<template>
  <div>
    <fullscreen-dialog
      v-model="model"
      :title="employeeFullName + ' - ' + $t('credit_transactions')"
      :subtitle="companyName"
      icon="mdi-credit-card"
      hide-footer
      hide-padding
      @close="$emit('close')"
    >
      <template v-slot:filters>
        <page-toolbar v-if="!device.isMobile" :searchable="false">
          <template v-slot:filters>
            <v-col cols="6" lg="auto">
              <text-field
                v-model="search"
                kind="search"
                :label="$t('search')"
                color="white"
                hide-details
              />
            </v-col>

            <v-col>
              <v-row justify="end" no-gutters>
                <excel-button
                  @click="exportExcel"
                  :loading="isExcelDownloading"
                  class="mr-2"
                />
                <print-button @click="print" :loading="isPrinting" />
              </v-row>
            </v-col>
          </template>
        </page-toolbar>

        <div v-else class="pa-3">
          <text-field
            v-model="search"
            kind="search"
            :label="$t('search')"
            hide-details
            class="mb-3"
          />
          <div class="d-flex justify-end" style="gap: 8px">
            <div>
              <print-button @click="print" />
            </div>
            <div>
              <excel-button @click="exportExcel" :loading="isExcelDownloading" />
            </div>
          </div>
        </div>
      </template>

      <template v-if="device.isMobile">
        <empty-state
          v-if="!loading && filteredLimits.length === 0"
          icon="mdi-credit-card-off"
          :title="$t('no_transactions')"
          :subtitle="
            hasFilters ? $t('try_different_filters') : $t('no_limit_credit_state_yet')
          "
        >
          <primary-button @click="dialog = true">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("tooltip_add_credit") }}
          </primary-button>
        </empty-state>

        <mobile-data-cards
          v-else
          :headers="mobileHeaders"
          :items="filteredLimits"
          :loading="loading"
          item-key="limit_id"
          show-filters
          :fields-per-row="null"
          class="mt-3"
          :show-row-number="false"
          :bold-fields="['limit_name']"
        >
          <template v-slot:item.limit_name="{ item }">
            <div class="font-weight-medium">{{ item.limit_name }}</div>
            <div class="text-caption text--secondary">ID: {{ item.limit_id }}</div>
          </template>
          <template v-slot:item.total_amount="{ item }">
            {{ formatAmount(item.total_amount) }}
          </template>
          <template v-slot:item.total_spent="{ item }">
            {{ formatAmount(item.total_spent) }}
          </template>
          <template v-slot:item.total_paid="{ item }">
            {{ formatAmount(item.total_paid) }}
          </template>
        </mobile-data-cards>

        <div class="fixed-bottom-button">
          <v-btn color="primary" block @click="dialog = true">
            <v-icon class="mr-2">mdi-plus</v-icon>
            {{ $t("tooltip_add_credit") }}
          </v-btn>
        </div>
      </template>

      <!-- Desktop View -->
      <template v-else>
        <empty-state
          v-if="!loading && filteredLimits.length === 0"
          icon="mdi-credit-card-off"
          :title="$t('no_transactions')"
          :subtitle="
            hasFilters ? $t('try_different_filters') : $t('no_limit_credit_state_yet')
          "
        >
          <primary-button @click="dialog = true">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("tooltip_add_credit") }}
          </primary-button>
        </empty-state>

        <v-data-table
          v-else
          :headers="headers"
          :items="filteredLimits"
          :loading="loading"
          :items-per-page="10"
          :expanded.sync="expandedLimits"
          item-key="limit_id"
          show-expand
          class="ordera-box-shadow striped-table"
        >
          <template v-slot:item.limit_name="{ item }">
            <div class="font-weight-medium">{{ item.limit_name }}</div>
            <div class="text-caption text--secondary">ID: {{ item.limit_id }}</div>
          </template>

          <template v-slot:item.total_amount="{ item }">
            {{ formatAmount(item.total_amount) }}
          </template>

          <template v-slot:item.total_spent="{ item }">
            {{ formatAmount(item.total_spent) }}
          </template>

          <template v-slot:item.total_paid="{ item }">
            {{ formatAmount(item.total_paid) }}
          </template>

          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <v-btn icon small @click.stop="toggleExpand(item, isExpanded, expand)">
              <v-icon>{{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </v-btn>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <div class="limit-transactions-placeholder">
                <div
                  v-if="limitTransactionsLoading[item.limit_id]"
                  class="text-body-2 text--secondary"
                >
                  {{ $t("loading_limit_transactions") }}
                </div>
                <div
                  v-else-if="getLimitTransactionsData(item.limit_id)"
                  class="limit-transactions-wrap"
                >
                  <div class="limit-transaction-summary">
                    <div class="limit-transaction-stat">
                      <div class="text-caption text--secondary">
                        {{ $t("transactions") }}
                      </div>
                      <div class="font-weight-medium">
                        {{ getLimitTransactionsData(item.limit_id).total_transactions }}
                      </div>
                    </div>
                    <div class="limit-transaction-stat">
                      <div class="text-caption text--secondary">{{ $t("amount") }}</div>
                      <div class="font-weight-medium">
                        {{
                          formatAmount(
                            getLimitTransactionsData(item.limit_id).total_amount
                          )
                        }}
                      </div>
                    </div>
                  </div>

                  <v-data-table
                    :headers="transactionHeaders"
                    :items="getLimitTransactionsData(item.limit_id).transactions"
                    dense
                    hide-default-footer
                    :items-per-page="
                      getLimitTransactionsData(item.limit_id).transactions.length || 1
                    "
                    class="limit-transactions-table"
                  >
                    <template v-slot:item.created_at="{ item: transaction }">
                      {{ formatDateTime(transaction.created_at) }}
                    </template>

                    <template v-slot:item.date="{ item: transaction }">
                      {{ formatDate(transaction.date) }}
                    </template>

                    <template v-slot:item.details="{ item: transaction }">
                      <div class="font-weight-medium">
                        {{ getTransactionTitle(transaction) }}
                      </div>
                      <div v-if="transaction.notes" class="text-caption text--secondary">
                        {{ transaction.notes }}
                      </div>
                    </template>

                    <template v-slot:item.price="{ item: transaction }">
                      {{ formatAmount(transaction.price) }}
                    </template>

                    <template v-slot:item.total_amount="{ item: transaction }">
                      {{ formatAmount(transaction.total_amount) }}
                    </template>
                  </v-data-table>
                </div>
                <div v-else class="text-body-2 text--secondary">
                  {{ $t("expand_to_load_limit_transactions") }}
                </div>
              </div>
            </td>
          </template>
        </v-data-table>
      </template>

      <add-credit-dialog
        v-model="dialog"
        :employee-id="employee.id"
        @saved="fetchCurrentLimitState"
      />
    </fullscreen-dialog>

    <v-tooltip left v-if="!device.isMobile">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-if="model"
          fab
          fixed
          bottom
          right
          color="primary"
          large
          class="credit-fab primary-gradient"
          v-bind="attrs"
          v-on="on"
          @click="dialog = true"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <span>{{ $t("tooltip_add_credit") }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import limitService from "@/services/limit";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import AddCreditDialog from "./AddCreditDialog.vue";

export default {
  name: "CreditTransactions",
  components: {
    FullscreenDialog,
    PageToolbar,
    EmptyState,
    MobileDataCards,
    AddCreditDialog,
  },
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
  },
  mixins: [hasModel, hasPrinting, hasExcel],
  data() {
    return {
      limitStates: [],
      loading: false,
      dialog: false,
      search: "",
      expandedLimits: [],
      limitTransactions: {},
      limitTransactionsLoading: {},
      limitTransactionsLoaded: {},
    };
  },
  mounted() {
    this.fetchCurrentLimitState();
  },
  methods: {
    normalizeLimitState(item) {
      const currentState = item?.current_state || {};

      return {
        user_id: item?.user_id || "",
        limit_id: item?.limit?.id || null,
        limit_name: this.toLatin(item?.limit?.name || "-"),
        transaction_count: Number(currentState.transaction_count || 0),
        total_amount: this.parseAmount(currentState.total_amount),
        total_spent: this.parseAmount(currentState.total_spent),
        total_paid: this.parseAmount(currentState.total_paid),
      };
    },
    normalizeLimitTransactionsResponse(limitId, payload) {
      const transactions = Array.isArray(payload?.transactions)
        ? payload.transactions
        : [];

      return {
        limit_id: Number(payload?.limit_id || limitId),
        total_transactions: Number(
          payload?.total_transactions || transactions.length || 0
        ),
        total_amount: this.parseAmount(payload?.total_amount),
        transactions: transactions.map((item) => this.normalizeLimitTransaction(item)),
      };
    },
    normalizeLimitTransaction(item) {
      const userName = [item?.user?.name, item?.user?.lastname].filter(Boolean).join(" ");

      return {
        id: item?.id || null,
        created_at: item?.created_at || null,
        date: item?.date || null,
        limit_type: item?.limit_type || "",
        quantity: Number(item?.quantity || 0),
        price: this.parseAmount(item?.price),
        total_amount: this.parseAmount(item?.total_amount),
        shift: item?.shift || "",
        notes: item?.notes || "",
        orderable_name: this.toLatin(item?.orderable?.name || ""),
        user_name: userName,
      };
    },
    parseAmount(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    formatAmount(value) {
      return this.parseAmount(value).toFixed(2);
    },
    getTransactionTitle(transaction) {
      if (transaction?.limit_type === "credit") {
        return this.$t("credit_topup");
      }

      return transaction?.orderable_name || "-";
    },
    formatDateTime(value) {
      if (!value) return "-";

      return this.localeDate(value, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatDate(value) {
      if (!value) return "-";

      return this.localeDate(value, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    async fetchCurrentLimitState() {
      this.loading = true;
      try {
        const { data } = await limitService.getCurrentUserLimitState(this.employee.id);
        const items = data?.data || data || [];
        this.limitStates = items.map((item) => this.normalizeLimitState(item));
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async fetchLimitTransactions(limitId) {
      if (!limitId) return;
      if (
        this.limitTransactionsLoading[limitId] ||
        this.limitTransactionsLoaded[limitId]
      ) {
        return;
      }

      this.$set(this.limitTransactionsLoading, limitId, true);

      try {
        const { data } = await limitService.getLimitTransactions(limitId);
        this.$set(
          this.limitTransactions,
          limitId,
          this.normalizeLimitTransactionsResponse(limitId, data?.data || data || {})
        );
        this.$set(this.limitTransactionsLoaded, limitId, true);
      } catch (err) {
        console.error(err);
      } finally {
        this.$set(this.limitTransactionsLoading, limitId, false);
      }
    },
    toggleExpand(item, isExpanded, expand) {
      const nextState = !isExpanded;
      expand(nextState);

      if (nextState) {
        this.fetchLimitTransactions(item.limit_id);
      }
    },
    getLimitTransactionsData(limitId) {
      return this.limitTransactions[limitId] || null;
    },
    print() {
      const printHeaders = this.headers.map((h) => ({
        text: h.text,
        value: h.value,
      }));
      const printItems = this.filteredLimits.map((item) => ({
        limit_name: item.limit_name,
        transaction_count: item.transaction_count,
        total_amount: this.formatAmount(item.total_amount),
        total_spent: this.formatAmount(item.total_spent),
        total_paid: this.formatAmount(item.total_paid),
      }));
      this.printDocument({
        title: this.$t("credit_transactions"),
        subtitle: this.employeeFullName,
        tables: [
          {
            headers: printHeaders,
            items: printItems,
          },
        ],
      });
    },
    exportExcel() {
      const excelHeaders = this.headers.map((h) => ({
        text: h.text,
        value: h.value,
      }));
      const excelItems = this.filteredLimits.map((item) => ({
        limit_name: item.limit_name,
        transaction_count: item.transaction_count,
        total_amount: this.formatAmount(item.total_amount),
        total_spent: this.formatAmount(item.total_spent),
        total_paid: this.formatAmount(item.total_paid),
      }));
      this.downloadExcel({
        fileName: `${this.employeeFullName}_${this.$t("credit_transactions")}.xlsx`,
        sheets: [
          {
            name: this.$t("credit_transactions"),
            title: this.$t("credit_transactions"),
            subtitle: this.employeeFullName,
            headers: excelHeaders,
            items: excelItems,
          },
        ],
      });
    },
  },
  computed: {
    headers() {
      return [
        { text: this.$t("credit_limit"), value: "limit_name", cols: 12 },
        {
          text: this.$t("transactions"),
          value: "transaction_count",
          align: "center",
          cols: 6,
        },
        { text: this.$t("amount"), value: "total_amount", align: "right", cols: 6 },
        { text: this.$t("paid"), value: "total_spent", align: "right", cols: 6 },
        { text: this.$t("spent"), value: "total_paid", align: "right", cols: 6 },
      ];
    },
    transactionHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: this.$t("date"), value: "date" },
        { text: this.$t("warehouse.transaction"), value: "details" },
        { text: this.$t("quantity"), value: "quantity", align: "center" },
        { text: this.$t("price"), value: "price", align: "right" },
        { text: this.$t("amount"), value: "total_amount", align: "right" },
      ];
    },
    mobileHeaders() {
      return this.headers;
    },
    filteredLimits() {
      let result = this.limitStates;

      if (this.search && this.search.trim()) {
        const searchLower = this.translate(this.search.toLowerCase().trim());
        result = result.filter((item) => {
          return (
            this.translate(String(item.limit_name).toLowerCase()).includes(searchLower) ||
            String(item.limit_id || "").includes(searchLower) ||
            String(item.transaction_count).includes(searchLower) ||
            this.formatAmount(item.total_amount).includes(searchLower) ||
            this.formatAmount(item.total_spent).includes(searchLower) ||
            this.formatAmount(item.total_paid).includes(searchLower)
          );
        });
      }

      return result;
    },
    employeeFullName() {
      const name = this.employee?.name || "";
      const lastname = this.employee?.lastname || "";
      return this.toLatin(`${name} ${lastname}`.trim());
    },
    companyName() {
      return this.toLatin(this.employee?.company?.name || "");
    },
    hasFilters() {
      return Boolean(this.search && this.search.trim());
    },
  },
};
</script>

<style scoped>
.headline {
  font-weight: 600;
}

.credit-fab {
  z-index: 1000 !important;
  margin-bottom: 16px !important;
}

.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 5;
}

.mobile-actions-bar {
  background-color: #f5f5f5;
}

.limit-transactions-placeholder {
  padding: 16px 8px;
}

.limit-transactions-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.limit-transaction-summary {
  display: flex;
  gap: 24px;
}

.limit-transaction-stat {
  min-width: 120px;
}

.limit-transactions-table {
  background: #fafafa;
}
</style>
